import { Button, Descriptions, Tag, Tooltip } from "antd";
import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { useAtom } from 'jotai';
import { showMapDataDetailPanelAtom, selectedMapDataAtom } from "../../stores/mapStore";
import { FaXmark } from "react-icons/fa6";
import {
  PiInfo,
  PiFileText,
  PiChartBar,
  PiTag,
  PiFolder,
  PiMapPin,
  PiMapTrifold,
  PiImage,
  PiPolygon
} from "react-icons/pi";
import { MapData_Base, MapData_ProcessStatus, MapData_ModelUtil } from '~/common/typescript/models/MapData';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import { Rnd } from 'react-rnd';

export const MapDataDetailPanel = () => {
  const [showMapDataDetailPanel, setShowMapDataDetailPanel] = useAtom(showMapDataDetailPanelAtom);
  const [selectedMapData, setSelectedMapData] = useAtom(selectedMapDataAtom);

  const [metadata, setMetadata] = useState<any>({});

  useEffect(() => {
    if (!selectedMapData) return;

    try {
      const parsed = JSON.parse(selectedMapData.metadata || '{}');
      setMetadata(parsed);
    } catch (error) {
      setMetadata({});
    }
  }, [selectedMapData]);

  if (!showMapDataDetailPanel || !selectedMapData) {
    return null;
  }

  const handleClose = () => {
    setShowMapDataDetailPanel(false);
    setSelectedMapData(null);
  };

  const getStatusTag = (status?: MapData_ProcessStatus) => {
    if (!status) return <Tag color="default">Không xác định</Tag>;

    const colorMap: Record<string, string> = {
      [MapData_ProcessStatus.PENDING]: 'gold',
      [MapData_ProcessStatus.SUBMITED]: 'gold',
      [MapData_ProcessStatus.PROCESSING]: 'blue',
      [MapData_ProcessStatus.SUCCESS]: 'green',
      [MapData_ProcessStatus.FAILED]: 'red',
      [MapData_ProcessStatus.WAITING_USER_INPUT]: 'orange',
    };

    const text = MapData_ModelUtil.getTextStatus(status);
    return <Tag color={colorMap[status]}>{text}</Tag>;
  };

  const getDriverIcon = (driver?: string) => {
    if (!driver) return <PiFolder style={{ marginRight: 6, verticalAlign: -2 }} />;
    if (driver.includes('ESRI') || driver.includes('Shapefile')) {
      return <PiMapPin style={{ marginRight: 6, color: '#1677ff', verticalAlign: -2 }} />;
    }
    if (driver.includes('GeoJSON')) {
      return <PiMapTrifold style={{ marginRight: 6, color: '#52c41a', verticalAlign: -2 }} />;
    }
    if (driver.includes('Raster') || driver.includes('GTiff')) {
      return <PiImage style={{ marginRight: 6, color: '#fa8c16', verticalAlign: -2 }} />;
    }
    return <PiFolder style={{ marginRight: 6, verticalAlign: -2 }} />;
  };

  const driverShortName = metadata?.driverShortName || '-';
  const isVector = metadata?.isVector;
  const isRaster = metadata?.isRaster;
  const displayName = selectedMapData.title || selectedMapData.name || 'Dữ liệu bản đồ';

  return (
    <Rnd
      default={{
        x: Math.max(10, window.innerWidth - 400),
        y: 32,
        width: 380,
        height: Math.min(580, window.innerHeight - 80),
      }}
      minWidth={300}
      minHeight={250}
      dragHandleClassName="draggable-header"
      cancel=".no-drag, .no-drag *, button, input, textarea, select, a"
      bounds="window"
      enableResizing={{
        bottom: true,
        bottomRight: true,
        right: true,
        top: false,
        topLeft: false,
        topRight: false,
        left: false,
        bottomLeft: false,
      }}
      style={{ zIndex: 1000 }}
    >
      <UI_Wrapper>
        <UI_Header className="draggable-header" style={{ cursor: 'move', userSelect: 'none' }}>
          <UI_HeaderLeft className="no-drag" style={{ cursor: 'default' }}>
            <UI_HeaderIcon>
              <PiFileText />
            </UI_HeaderIcon>
            <UI_HeaderTitle title={`Chi tiết: ${displayName}`}>
              Chi tiết: {displayName}
            </UI_HeaderTitle>
          </UI_HeaderLeft>
          <UI_HeaderRight className="no-drag" style={{ cursor: 'default' }}>
            <Tooltip title="Đóng panel chi tiết">
              <UI_HeaderRight_item onClick={handleClose} role="button" aria-label="Đóng">
                <FaXmark />
              </UI_HeaderRight_item>
            </Tooltip>
          </UI_HeaderRight>
        </UI_Header>

        <UI_Content>
          {/* Thông tin chung */}
          <UI_Section>
            <UI_SectionTitle>
              <PiInfo style={{ color: '#1677ff', fontSize: 16 }} />
              <span>Thông tin chung</span>
            </UI_SectionTitle>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Tên">
                <div
                  style={{
                    maxWidth: 230,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={displayName}
                >
                  {displayName}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Mô tả">{selectedMapData.description || '-'}</Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                {getStatusTag(selectedMapData.processStatus)}
              </Descriptions.Item>
              <Descriptions.Item label="Chủ sở hữu">
                {selectedMapData.owner?.username || selectedMapData.owner?.firstName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày tạo"><DateTimeViewer date={selectedMapData.createdAt} initialMode="relative" /></Descriptions.Item>
              <Descriptions.Item label="Cập nhật"><DateTimeViewer date={selectedMapData.updatedAt} initialMode="relative" /></Descriptions.Item>
              <Descriptions.Item label="Danh mục">
                {selectedMapData.category ? <Tag color="blue">{selectedMapData.category}</Tag> : '-'}
              </Descriptions.Item>
            </Descriptions>
          </UI_Section>

          {/* Thông tin kỹ thuật */}
          <UI_Section>
            <UI_SectionTitle>
              <PiChartBar style={{ color: '#52c41a', fontSize: 16 }} />
              <span>Thông tin kỹ thuật</span>
            </UI_SectionTitle>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Loại dữ liệu">
                {isVector ? (
                  <Tag color="geekblue" icon={<PiPolygon style={{ verticalAlign: -1, marginRight: 4 }} />}>
                    Vector
                  </Tag>
                ) : isRaster ? (
                  <Tag color="orange" icon={<PiImage style={{ verticalAlign: -1, marginRight: 4 }} />}>
                    Raster
                  </Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Driver">
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {getDriverIcon(driverShortName)}
                  <span>{driverShortName}</span>
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="ID">{selectedMapData.id}</Descriptions.Item>
            </Descriptions>
          </UI_Section>

          {/* Tags */}
          {selectedMapData.tags && selectedMapData.tags.length > 0 && (
            <UI_Section>
              <UI_SectionTitle>
                <PiTag style={{ color: '#fa8c16', fontSize: 16 }} />
                <span>Tags</span>
              </UI_SectionTitle>
              <UI_TagsWrapper>
                {selectedMapData.tags.map((tag: string, index: number) => (
                  <Tag key={index} color="default">{tag}</Tag>
                ))}
              </UI_TagsWrapper>
            </UI_Section>
          )}
        </UI_Content>
      </UI_Wrapper>
    </Rnd>
  );
};

export default memo(MapDataDetailPanel);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: var(--shadows-paneShadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const UI_Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
  min-width: 0;
  box-sizing: border-box;
`;

const UI_HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const UI_HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1677ff;
  font-size: 18px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const UI_HeaderTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  width: 100%;
  display: block;
  color: #1e293b;
`;

const UI_HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
`;

const UI_HeaderRight_item = styled.div`
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #595959;

  &:hover {
    background-color: #f5f5f5;
    color: #1f1f1f;
  }
`;

const UI_Content = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
`;

const UI_Section = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const UI_SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UI_TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
